import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box, Columns, LoadingModule,
} from '@domain-group/suit-theme'

import useAgency from '@hooks/useAgency'
import useAgencySelector from '@hooks/useAgencySelector'
import useOrderItemTaskTypes from '@hooks/useOrderItemTaskTypes'
import useProviders from '@hooks/useProviders'
import { generateUrls } from '@hooks/useOrderItemTaskType'

import ListBlankState from '@components/ListBlankState'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageHeader from '@components/PageHeader'

import Provider from '../Provider'

const PageContent = () => {
  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { selectedAgencyId } = useAgencySelector()
  const { taskPricingIndexUrl } = generateUrls({ prefixRoute: `agencies/${selectedAgencyId}/manage` })

  const providersPayload = useProviders({
    filters: {
      service_type: 'taskable',
    },
    performHttpRequests: !!selectedAgencyId,
    requestOptions: {
      include_provider_alternate_logo: true,
      include_provider_service_types: true,
    },
  })

  const {
    filteredProviders,
    hasFilteredProviders,
    loading,
  } = providersPayload

  const { filteredOrderItemTaskTypes } = useOrderItemTaskTypes({
    filters: {
      allowed_task_types: ['clean', 'repair', 'move', 'other'],
    },
    performHttpRequests: true,
  })

  const { agency, callbacks: { editTaskPricing } } = useAgency({ id: selectedAgencyId })
  const taskPrices = digObject(agency, 'options.task_prices', {})

  return (
    <>
      <Helmet>
        <title>Task Pricing | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="current"
        nestedNavigation
        tabBarItems={[
          {
            href: taskPricingIndexUrl,
            icon: faCheckCircle,
            key: 'current',
            title: 'Task Pricing',
          },
        ]}
        title="Task Pricing"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.taskPricing.banner" />

            {/*
             // @ts-expect-error TS2322 */}
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredProviders && <ListBlankState />}

            {!loading && hasFilteredProviders && (
              <Box flexDirection="column">
                {filteredProviders.map(provider => (
                  <Provider
                    key={provider.id}
                    callbacks={{ editTaskPricing }}
                    orderItemTaskTypes={filteredOrderItemTaskTypes}
                    provider={provider}
                    taskPrices={taskPrices[provider.id]}
                  />
                ))}
              </Box>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.taskPricing.sidebar" />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
